import {
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { RequestDrawers as DrawersType } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { modelUtils, QuoteStatusConfirmedLike } from '@pelicargo/utils';
import { cloneElement, Fragment, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { AirlineBookingDrawer } from '../components/Drawers/AirlineBookingDrawer';
import { BookingCancelDrawer } from '../components/Drawers/BookingCancelDrawer';
import { BookingChangeDrawer } from '../components/Drawers/BookingChangeDrawer';
import { BookingConfirmDrawer } from '../components/Drawers/BookingConfirmDrawer';
import { ContactFormDrawer } from '../components/Drawers/ContactFormDrawer';
import { GenericEmailDrawer } from '../components/Drawers/GenericEmailDrawer';
import { NewRateDrawer } from '../components/Drawers/NewRateDrawer';
import { ProviderQuotingDrawer } from '../components/Drawers/ProviderQuotingDrawer';
import { QuoteUpdateDrawer } from '../components/Drawers/QuoteUpdateDrawer';
import { RequestClarificationsDrawer } from '../components/Drawers/RequestClarificationsDrawer';
import { RequestScheduleDrawer } from '../components/Drawers/RequestScheduleDrawer';
import { StatusUpdateEmailDrawer } from '../components/Drawers/StatusUpdateEmailDrawer';
import { RequestDetail } from '../components/RequestDetails/RequestDetail';
import { RequestDrawers, useRequestDrawersContext } from '../components/RequestDrawers';
import { CurrentRequestProvider } from '../context/CurrentRequestContext';
import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { useDashboard } from '../hooks/useDashboard';
import { useParams } from '../hooks/useParams';

const DRAWERS: Record<DrawersType, any> = {
  ProviderQuoting: <ProviderQuotingDrawer />,
  AirlineBooking: <AirlineBookingDrawer />,
  RequestClarifications: <RequestClarificationsDrawer />,
  QuoteUpdate: <QuoteUpdateDrawer />,
  BookingConfirmation: <BookingConfirmDrawer />,
  BookingChange: <BookingChangeDrawer />,
  BookingCancel: <BookingCancelDrawer />,
  NewRate: <NewRateDrawer />,
  Contact: <ContactFormDrawer />,
  RequestSchedule: <RequestScheduleDrawer />,
  GenericEmailToFF: <GenericEmailDrawer />,
  StatusUpdate: <StatusUpdateEmailDrawer />,
};

export const SingleRequestScreen = () => (
  <CurrentRequestProvider>
    <SingleRequestScreenContent />
  </CurrentRequestProvider>
);

type RequestType = ReturnType<typeof useCurrentRequest>['request'];
type QuotesType = ReturnType<typeof useCurrentRequest>['quotes'];

const hasBookedLegs = (request: RequestType, quotes: QuotesType) =>
  request?.booked_quote_variant_id != null ||
  !!quotes?.some((quote) => QuoteStatusConfirmedLike.includes(quote?.status));

type RequestTab = { key: string; label: string };

// Build the ordered list of tabs that will actually render, so the Chakra Tabs
// `index` (which counts only rendered tabs) and the matching TabPanels stay in sync.
const getRequestTabs = (request: RequestType, quotes: QuotesType, isDevMode: boolean): RequestTab[] => {
  const tabs: RequestTab[] = [];

  if (modelUtils.request.requiresAirFreight(request)) tabs.push({ key: 'airline', label: 'Airline' });
  if (modelUtils.request.requiresInland(request)) tabs.push({ key: 'inland', label: 'Inland' });
  if (hasBookedLegs(request, quotes)) tabs.push({ key: 'booking', label: 'Booking' });
  if (isDevMode) tabs.push({ key: 'emails', label: 'Emails' });

  return tabs;
};

const SingleRequestScreenContent = () => {
  const dashboard = useDashboard();

  const { requestId: id } = useParams<{ requestId: number }>();
  const { request, quotes, isLoadingRequest } = useCurrentRequest();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  useEffect(() => {
    const isDuplicate = !!request?.main_request_id;

    if (isDuplicate) onOpen();
    else onClose();
  }, [request?.main_request_id, onOpen, onClose]);

  if (isLoadingRequest || Number(id) !== Number(request?.id)) {
    return (
      <Center flex={1} h="100vh">
        <Spinner />
      </Center>
    );
  }

  const tabs = getRequestTabs(request, quotes, dashboard.isDevMode);
  const currentSegment = location.pathname.split('/').at(-1);
  const currentIndex = tabs.findIndex((tab) => tab.key === currentSegment);

  return (
    <Flex direction="row" flex={1} h="100vh" position="relative">
      <RequestDrawers>
        <Show if={isOpen}>
          <Flex top="0" left="0" right="0" bottom="0" position="absolute" align="center" justify="center">
            <Flex bg="#000" opacity="0.4" top="0" left="0" right="0" bottom="0" position="absolute" zIndex="overlay" />
            <Stack zIndex="modal" bg="bg.surface" p="8" borderRadius="md" maxW="md" spacing="4">
              <Heading>Duplicate of R{request?.main_request_id}</Heading>
              <Stack>
                <Text>
                  This request has been marked as a duplicate of R{request?.main_request_id}. Do you want to view the
                  original request?
                </Text>
              </Stack>
              <Stack direction="row" justify="flex-end" spacing="2">
                <Button variant="outline" onClick={onClose}>
                  Hide overlay
                </Button>
                <Button variant="primary" as={Link} to={`/requests/${request?.main_request_id}`}>
                  View original
                </Button>
              </Stack>
            </Stack>
          </Flex>
        </Show>
        <RequestDetail />
        <Tabs
          isFitted
          variant="line"
          colorScheme="primary"
          flex={1}
          isLazy
          index={currentIndex}
          display="flex"
          flexDirection="column"
        >
          <TabList>
            {tabs.map((tab) => (
              <Tab as={Link} to={`/requests/${id}/${tab.key}`} key={tab.key}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels display="flex" flex={1} h="full" flexDir="column" overflowY="hidden">
            {tabs.map((tab) => (
              <TabPanel key={tab.key} overflowY="hidden" display="flex" flex={1} p={0}>
                <Outlet />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
        <Drawers />
      </RequestDrawers>
    </Flex>
  );
};

const Drawers = () => {
  const { getIsModalOpen, getModalData } = useRequestDrawersContext();

  const drawers = Object.entries(DRAWERS);

  return (
    <>
      {drawers.map(([key, ele]) => {
        const isOpen = getIsModalOpen(key as DrawersType);
        const modalData = getModalData(key as DrawersType);

        return (
          <Fragment key={key}>
            <Show if={isOpen}>{cloneElement(ele, { ...modalData })}</Show>
          </Fragment>
        );
      })}
    </>
  );
};
